import { useState, type ReactNode } from "react";
import { toast } from "react-hot-toast";

import {
  ClickableTableRow,
  OpenButton,
  OpenIcon,
  pageCount,
  pageItems,
  Pagination,
  Search,
  Selector,
  SortableTableHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  type SelectorOption,
} from "../../components/ui";
import { Empty } from "../../components/WorkspacePrimitives";
import { startArtifactDownload } from "../../lib/artifactDownload";
import {
  useAgentDocumentLink,
  type AgentDocument,
  type AgentStoredDocument,
} from "../api/e2e";
import { documentSourceDomain } from "../lib/documents";
import { useAgentDocumentOpenAction } from "./AgentDocumentActionContext";
import AgentDocumentHierarchyBadge from "./AgentDocumentHierarchyBadge";

export type DocumentTypeFilter =
  | "all"
  | "agent_fetched"
  | "analyst_upload"
  | "tucker_document";
export type DocumentSort =
  | "release_desc"
  | "release_asc"
  | "name_asc"
  | "name_desc";

export const DOCUMENT_SORTS: Record<
  DocumentSort,
  { sortBy: "name" | "release_date"; sortOrder: "asc" | "desc" }
> = {
  release_desc: { sortBy: "release_date", sortOrder: "desc" },
  release_asc: { sortBy: "release_date", sortOrder: "asc" },
  name_asc: { sortBy: "name", sortOrder: "asc" },
  name_desc: { sortBy: "name", sortOrder: "desc" },
};

export interface DocumentContextColumn {
  header: ReactNode;
  render: (document: AgentStoredDocument) => ReactNode;
  className?: string;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024)
    return `${Math.max(1, Math.round(bytes / 1024)).toLocaleString()} KB`;
  return `${(bytes / (1024 * 1024)).toLocaleString(undefined, { maximumFractionDigits: 1 })} MB`;
}

export default function AgentDocumentCatalog({
  companyId,
  documents,
  isFetching,
  search,
  onSearchChange,
  documentType,
  onDocumentTypeChange,
  sort,
  onSortChange,
  actions,
  includeOntology = true,
  showCaptureCount = true,
  contextColumn,
  selectedDocumentId,
  onSelectDocument,
  emptyMessage,
  pageSize = 25,
}: {
  companyId: string;
  documents: AgentDocument[];
  isFetching: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  documentType: DocumentTypeFilter;
  onDocumentTypeChange: (value: DocumentTypeFilter) => void;
  sort: DocumentSort;
  onSortChange: (value: DocumentSort) => void;
  actions?: ReactNode;
  includeOntology?: boolean;
  showCaptureCount?: boolean;
  contextColumn?: DocumentContextColumn;
  selectedDocumentId?: string | null;
  onSelectDocument?: (document: AgentStoredDocument) => void;
  emptyMessage: string;
  pageSize?: number;
}) {
  const link = useAgentDocumentLink();
  const hostOpenDocument = useAgentDocumentOpenAction();
  const [openingDocumentId, setOpeningDocumentId] = useState<string | null>(
    null,
  );
  const [page, setPage] = useState(1);
  const selectedSort = DOCUMENT_SORTS[sort];
  const nameDirection =
    selectedSort.sortBy === "name" ? selectedSort.sortOrder : null;
  const releaseDirection =
    selectedSort.sortBy === "release_date" ? selectedSort.sortOrder : null;
  const currentPage = Math.min(page, pageCount(documents.length, pageSize));
  const visibleDocuments = pageItems(documents, currentPage, pageSize);
  const hasFilters = search.trim() !== "" || documentType !== "all";
  const documentTypeOptions: readonly SelectorOption<DocumentTypeFilter>[] = [
    { value: "all", label: "All sources" },
    { value: "agent_fetched", label: "Agent fetched" },
    { value: "analyst_upload", label: "Analyst upload" },
    ...(includeOntology
      ? [{ value: "tucker_document" as const, label: "Tucker ontology" }]
      : []),
  ];

  const openDocument = async (document: AgentStoredDocument) => {
    try {
      if (hostOpenDocument) {
        setOpeningDocumentId(document.id);
        await hostOpenDocument(document);
        return;
      }
      const capability = await link.mutateAsync({
        companyId,
        documentId: document.id,
      });
      startArtifactDownload(capability.url);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Document download failed.",
      );
    } finally {
      setOpeningDocumentId(null);
    }
  };

  return (
    <>
      <div
        className="filter-bar company-document-controls"
        aria-label="Document catalog controls"
      >
        <Search
          value={search}
          onValueChange={(value) => {
            onSearchChange(value);
            setPage(1);
          }}
          placeholder="Search documents"
          aria-label="Search company documents"
        />
        <Selector<DocumentTypeFilter>
          options={documentTypeOptions}
          value={documentType}
          onValueChange={(value) => {
            onDocumentTypeChange(value);
            setPage(1);
          }}
          ariaLabel="Filter documents by source"
        />
        {actions}
      </div>
      {documents.length === 0 ? (
        <Empty>
          {hasFilters ? "No documents match these filters." : emptyMessage}
        </Empty>
      ) : (
        <div className="company-table-wrap company-documents-wrap">
          <Table
            className={[
              "company-documents",
              showCaptureCount
                ? ""
                : "company-documents--without-capture-count",
              contextColumn ? "company-documents--with-context" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            aria-busy={isFetching}
          >
            <TableHead>
              <TableRow>
                <SortableTableHeader
                  direction={nameDirection}
                  onSort={() => {
                    onSortChange(
                      sort === "name_asc" ? "name_desc" : "name_asc",
                    );
                    setPage(1);
                  }}
                >
                  Document
                </SortableTableHeader>
                <SortableTableHeader
                  direction={releaseDirection}
                  onSort={() => {
                    onSortChange(
                      sort === "release_desc" ? "release_asc" : "release_desc",
                    );
                    setPage(1);
                  }}
                >
                  Released
                </SortableTableHeader>
                {contextColumn && (
                  <TableHeader className={contextColumn.className}>
                    {contextColumn.header}
                  </TableHeader>
                )}
                {showCaptureCount && <TableHeader>Captured by</TableHeader>}
                <TableHeader>Size</TableHeader>
                <TableHeader aria-label="Open" />
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleDocuments.map((document) => {
                const selected = selectedDocumentId === document.id;
                const cells = (
                  <>
                    <TableCell className="company-documents__description">
                      <div className="company-documents__cell-stack">
                        {onSelectDocument ? (
                          <button
                            type="button"
                            className="company-documents__peek"
                            aria-label={`Open side peek for ${document.name}, file ${document.filename}`}
                            onClick={() => onSelectDocument(document)}
                          >
                            <strong title={document.name}>
                              {document.name}
                            </strong>
                          </button>
                        ) : (
                          <strong title={document.name}>{document.name}</strong>
                        )}
                        <small title={document.filename}>
                          {document.filename}
                        </small>
                        <AgentDocumentHierarchyBadge
                          hierarchy={document.hierarchy}
                        />
                        {document.source_url ? (
                          <a
                            href={document.source_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {documentSourceDomain(document.source_url)}{" "}
                            <OpenIcon />
                          </a>
                        ) : (
                          <small>{documentSourceDomain(null)}</small>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{document.release_date ?? "—"}</TableCell>
                    {contextColumn && (
                      <TableCell className={contextColumn.className}>
                        {contextColumn.render(document)}
                      </TableCell>
                    )}
                    {showCaptureCount && (
                      <TableCell className="company-documents__runs">
                        {document.source_type === "tucker_document" ? (
                          <>Run start</>
                        ) : (
                          <>
                            {document.run_ids.length.toLocaleString()} run
                            {document.run_ids.length === 1 ? "" : "s"}
                          </>
                        )}
                      </TableCell>
                    )}
                    <TableCell className="company-documents__file">
                      <div className="company-documents__cell-stack">
                        <span>{formatBytes(document.size_bytes)}</span>
                        <small title={`SHA-256 ${document.sha256}`}>
                          SHA-256 {document.sha256.slice(0, 8)}…
                        </small>
                      </div>
                    </TableCell>
                    <TableCell className="company-catalog__open">
                      <OpenButton
                        label={`Open ${document.name}, file ${document.filename}`}
                        disabled={link.isPending || openingDocumentId !== null}
                        loading={
                          openingDocumentId === document.id ||
                          (link.isPending &&
                            link.variables?.documentId === document.id)
                        }
                        onClick={() => void openDocument(document)}
                      />
                    </TableCell>
                  </>
                );
                const className = [
                  "company-documents__row",
                  selected ? "is-selected" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return onSelectDocument ? (
                  <ClickableTableRow
                    key={document.id}
                    className={className}
                    aria-current={selected ? "true" : undefined}
                    onActivate={() => onSelectDocument(document)}
                  >
                    {cells}
                  </ClickableTableRow>
                ) : (
                  <TableRow key={document.id} className={className}>
                    {cells}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Pagination
            page={currentPage}
            pageSize={pageSize}
            total={documents.length}
            ariaLabel="Document pages"
            onPageChange={setPage}
          />
        </div>
      )}
    </>
  );
}
